import React, { useState, useEffect, useCallback, useMemo } from 'react';
import APIService from '../services/api';
import ErpiaSelect from './ErpiaSelect';
import { confirmErpia } from './ConfirmDialog';
import { CHAMPS_COLLABORATEUR } from './crm/PlacementInfosCollaborateur';

/**
 * Forms — les formulaires qu'on envoie remplir en ligne.
 *
 * Le parcours complet : on compose un formulaire ici, on l'envoie par LIEN
 * (pas un PDF), la personne le remplit en ligne, ses réponses reviennent —
 * le PDF rempli d'un côté, les valeurs de l'autre, qui atterrissent dans la
 * fiche ressource.
 *
 * D'où le parti pris de l'écran : chaque champ montre OÙ SA RÉPONSE ATTERRIT.
 * Un champ qui n'atterrit nulle part reste du papier ; c'est la destination
 * qui fait la différence entre un questionnaire et une fiche qui se remplit
 * toute seule.
 *
 * ON N'ENVOIE PAS « UN FORMULAIRE », ON L'ENVOIE À QUELQU'UN. Le lien est
 * personnel, à usage unique, et les réponses atterrissent sur LA FICHE du
 * destinataire : sans lui, le lien ne mène nulle part. L'écran demande donc
 * qui, avant d'envoyer — et montre ensuite qui a reçu, qui a répondu, qui
 * reste en attente.
 *
 * Routes (tolérées absentes) :
 *   GET/POST/PUT/DELETE /forms · GET /forms/destinations
 *   POST /forms/:id/envoyer { resource_id } → { lien, email_envoye, … }
 *   GET  /forms/:id/envois → [{ destinataire, statut, envoye_le, recu_le, … }]
 *   GET  /resources (les destinataires possibles)
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} aria-hidden="true" />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

/* Vocabulaire de SECOURS : types et destinations sont publiés par le serveur
   (GET /forms/destinations). Ces tables ne servent que le temps de sa réponse,
   ou s'il se tait — jamais à le contredire. */
const TYPES_SECOURS = [
  ['texte', 'Texte court'], ['texte_long', 'Texte long'], ['date', 'Date'],
  ['nombre', 'Nombre'], ['email', 'Email'], ['tel', 'Téléphone'],
  ['choix', 'Liste de choix'], ['fichier', 'Fichier à joindre'],
];
const DESTINATIONS_SECOURS = CHAMPS_COLLABORATEUR.map(c => [c.cle, c.label]);
/* Le serveur peut servir ses listes en chaînes ou en { cle, libelle }. */
const paire = (v) => (typeof v === 'string'
  ? [v, v.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())]
  : [String(v?.cle ?? v?.key ?? ''), String(v?.libelle ?? v?.label ?? v?.cle ?? '')]);
const TYPE_PAR_CLE = { date_naissance: 'date', avs: 'texte', iban: 'texte', npa: 'texte' };

const champVide = () => ({ id: `n${Date.now()}${Math.random().toString(36).slice(2, 6)}`, libelle: '', type: 'texte', destination: '', obligatoire: false, options: '' });
const formVide = () => ({ nom: '', description: '', champs: [champVide()] });
const fmtDate = (v) => { const d = v ? new Date(v) : null; return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('fr-CH', { day: 'numeric', month: 'short', year: 'numeric' }) : null; };
const nomDe = (r) => `${r?.prenom || ''} ${r?.nom || ''}`.trim() || r?.email || `Fiche ${r?.id}`;
// L'état d'un envoi, dit comme on le dirait à voix haute — « envoyé » ne
// distingue pas ce qui est parti de ce qui est resté bloqué faute d'email.
const ETAT_ENVOI = {
  recu: ['A répondu', 'check-circle-fill', 'ok'],
  envoye: ['En attente de réponse', 'hourglass-split', 'attente'],
  non_envoye: ["L'email n'est pas parti", 'exclamation-triangle-fill', 'bloque'],
};

export default function Forms({ showNotification }) {
  const [liste, setListe] = useState(null);    // null = chargement
  const [absent, setAbsent] = useState(false);
  const [erreur, setErreur] = useState('');
  const [form, setForm] = useState(null);      // formulaire en édition
  const [busy, setBusy] = useState(false);
  const [lien, setLien] = useState(null);      // { url, email_envoye, motif }
  const [copie, setCopie] = useState(false);
  const [types, setTypes] = useState(null);            // [[cle, libelle]] | null
  const [destinations, setDestinations] = useState(null);
  const [usages, setUsages] = useState([]);   // [{ cle, libelle, automatique, description }]
  const [ressources, setRessources] = useState(null);  // destinataires possibles (null = pas encore lues)
  const [ressourcesRefus, setRessourcesRefus] = useState(false);  // 403 : pas administrateur
  const [envoi, setEnvoi] = useState(null);   // { form, resource_id, busy } — le choix du destinataire
  const [envois, setEnvois] = useState({});   // form.id -> [envois] | 'chargement' | 'absent'
  const [ouverts, setOuverts] = useState(() => new Set());  // cartes dont le suivi est déplié
  // `champ` du 400 est un chemin : champs[2].options, champs[0].destination…
  const [champErreur, setChampErreur] = useState(null);

  useEffect(() => {
    let annule = false;
    fetch(`${BASE}/forms/destinations`, { headers: authH() })
      .then(r => (r.ok ? r.json() : null))
      .then(j => {
        if (annule || !j) return;
        const d = Array.isArray(j.data) ? j.data : (Array.isArray(j.destinations) ? j.destinations : null);
        if (d) setDestinations(d.map(paire).filter(([k]) => k));
        const t = Array.isArray(j.types) ? j.types : null;
        if (t) setTypes(t.map(paire).filter(([k]) => k));
        const u = Array.isArray(j.usages) ? j.usages : null;
        if (u) setUsages(u.filter(x => x && (x.cle ?? x.key)).map(x => ({
          cle: String(x.cle ?? x.key), libelle: String(x.libelle ?? x.label ?? x.cle ?? x.key),
          automatique: !!x.automatique, description: x.description || '',
        })));
      })
      .catch(() => { /* les tables de secours prennent le relais */ });
    // Les destinataires possibles. Lues une fois : le choix se fait dans une
    // liste, pas en tapant un email — une adresse saisie à la main enverrait
    // un lien dont les réponses n'auraient aucune fiche où atterrir.
    fetch(`${BASE}/resources`, { headers: authH() })
      .then(async (r) => {
        if (annule) return;
        // 401/403 : « aucune fiche » et « vous n'y avez pas accès » ne se
        // corrigent pas pareil. Dire le premier à la place du second envoie
        // créer des fiches qui existent déjà.
        if ([401, 403].includes(r.status)) { setRessourcesRefus(true); setRessources([]); return; }
        const j = r.ok ? await r.json().catch(() => null) : null;
        const arr = j?.data || j?.resources || (Array.isArray(j) ? j : []);
        setRessources(Array.isArray(arr) ? arr.filter(x => x && x.statut !== 'supprime') : []);
      })
      .catch(() => { if (!annule) setRessources([]); });
    return () => { annule = true; };
  }, []);
  const TYPES = types || TYPES_SECOURS;
  const DESTINATIONS = destinations || DESTINATIONS_SECOURS;

  const charger = useCallback(async () => {
    setErreur('');
    try {
      const r = await fetch(`${BASE}/forms`, { headers: authH() });
      if ([404, 405, 501].includes(r.status)) { setAbsent(true); setListe([]); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const arr = j.data || j.forms || (Array.isArray(j) ? j : []);
      setListe(Array.isArray(arr) ? arr : []);
    } catch (e) { setErreur(e.message); setListe([]); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const majChamp = (i, patch) => setForm(f => ({ ...f, champs: f.champs.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
  const deplacer = (i, sens) => setForm(f => {
    const cible = i + sens;
    if (cible < 0 || cible >= f.champs.length) return f;
    const champs = [...f.champs];
    [champs[i], champs[cible]] = [champs[cible], champs[i]];
    return { ...f, champs };
  });

  const enregistrer = async () => {
    const nom = String(form.nom || '').trim();
    if (!nom) { showNotification?.('Donnez un nom au formulaire.', 'error'); return; }
    const champs = form.champs
      .filter(c => String(c.libelle || '').trim())
      .map(c => ({
        // `cle` est ce qui relie un champ aux réponses DÉJÀ reçues : la
        // renvoyer telle quelle est la différence entre modifier un
        // formulaire et en repartir de zéro en orphelinant l'historique.
        ...(c.cle ? { cle: c.cle } : {}),
        libelle: c.libelle.trim(), type: c.type, destination: c.destination || null,
        obligatoire: !!c.obligatoire,
        ...(c.type === 'choix' ? { options: String(c.options || '').split('\n').map(o => o.trim()).filter(Boolean) } : {}),
      }));
    if (!champs.length) { showNotification?.('Ajoutez au moins un champ nommé.', 'error'); return; }
    setBusy(true);
    setChampErreur(null);
    try {
      const edition = form.id != null;
      const r = await fetch(`${BASE}/forms${edition ? `/${form.id}` : ''}`, {
        method: edition ? 'PUT' : 'POST', headers: jsonH(),
        body: JSON.stringify({
          nom, description: String(form.description || '').trim(), champs,
          ...(usages.length ? { usage: form.usage || null } : {}),
          ...(form.defaut !== undefined ? { defaut: !!form.defaut } : {}),
          ...(form.actif !== undefined ? { actif: !!form.actif } : {}),
        }),
      });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && !j.error) {
        showNotification?.('L\'enregistrement des formulaires arrive côté serveur — rien n\'a été enregistré.', 'info');
        return;
      }
      if (!r.ok || j.success === false) {
        const msg = j.error || j.message || `HTTP ${r.status}`;
        // Le serveur désigne le champ : champs[2].options, champs[0].destination.
        if (r.status === 400 && j.champ) { setChampErreur({ champ: String(j.champ), message: msg }); return; }
        throw new Error(msg);
      }
      showNotification?.(j.message || (edition ? 'Formulaire mis à jour.' : 'Formulaire créé.'), 'success');
      setForm(null);
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const supprimer = async (f) => {
    const ok = await confirmErpia({
      title: 'Supprimer ce formulaire',
      message: `« ${f.nom} » ne pourra plus être envoyé.`,
      detail: 'Les réponses déjà reçues restent sur les fiches où elles ont atterri.',
      confirmLabel: 'Supprimer le formulaire', tone: 'danger',
    });
    if (!ok) return;
    try {
      const r = await fetch(`${BASE}/forms/${f.id}`, { method: 'DELETE', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && !j.error) { showNotification?.('La suppression arrive côté serveur — rien n\'a été retiré.', 'info'); return; }
      if (r.status === 409) {
        // Déjà envoyé : le supprimer effacerait le lien vers des réponses
        // reçues. On propose ce que le serveur dit possible — le désactiver.
        const envois = Number(j.envois ?? 0) || 0;
        if (j.desactiver_possible) {
          const off = await confirmErpia({
            title: 'Désactiver au lieu de supprimer',
            message: j.error || j.message || `« ${f.nom} » a déjà été envoyé${envois ? ` ${envois} fois` : ''} et ne peut plus être supprimé.`,
            detail: 'Désactivé, il n\'est plus proposé à l\'envoi mais les réponses reçues restent rattachées.',
            confirmLabel: 'Désactiver le formulaire',
          });
          if (off) await basculerActif(f, false);
          return;
        }
        showNotification?.(j.error || j.message || 'Ce formulaire a déjà servi et ne peut plus être supprimé.', 'error');
        return;
      }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Formulaire supprimé.', 'success');
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
  };

  const basculerActif = async (f, actif) => {
    try {
      const r = await fetch(`${BASE}/forms/${f.id}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ actif }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(j.message || (actif ? 'Formulaire réactivé.' : 'Formulaire désactivé — il n\'est plus proposé à l\'envoi.'), 'success');
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
  };

  // ── L'ENVOI ────────────────────────────────────────────────────────────
  // Deux temps, parce qu'il y a deux décisions : à QUI, puis envoyer. Un
  // bouton qui enverrait d'un clic sans dire à qui ferait partir un email
  // personnel — avec un IBAN à saisir — chez la mauvaise personne, et le
  // lien précédent de celle-ci cesserait de fonctionner au passage.
  const ouvrirEnvoi = (f) => {
    setLien(null);
    setEnvoi({ form: f, resource_id: '', busy: false });
    // Les envois déjà faits sont lus MAINTENANT, pas au moment du clic final :
    // c'est ce qui permet d'avertir qu'un renvoi invalidera le lien précédent.
    // Attendre que l'utilisateur ait pensé à ouvrir « Qui l'a reçu » ferait
    // dépendre l'avertissement d'un geste qu'il n'a aucune raison de faire.
    if (envois[f.id] === undefined) chargerEnvois(f);
  };

  const envoyer = async () => {
    const f = envoi.form;
    const cible = (ressources || []).find(r => String(r.id) === String(envoi.resource_id));
    if (!cible) { showNotification?.('Choisissez la personne à qui envoyer ce formulaire.', 'error'); return; }
    // Un RENVOI invalide le lien précédent : si la personne était en train de
    // le remplir, son brouillon devient inaccessible. On le dit AVANT.
    const dejaEnvoye = (Array.isArray(envois[f.id]) ? envois[f.id] : [])
      .find(e => String(e.resource_id) === String(cible.id) && e.statut !== 'non_envoye');
    if (dejaEnvoye) {
      const ok = await confirmErpia({
        title: 'Renvoyer le formulaire',
        message: `${nomDe(cible)} a déjà reçu « ${f.nom} »${dejaEnvoye.statut === 'recu' ? ' et y a répondu' : ''}.`,
        detail: dejaEnvoye.statut === 'recu'
          ? 'Un nouvel envoi lui redemande tout et remplacera ses réponses actuelles.'
          : 'Le lien précédent cessera de fonctionner — s\'il l\'avait commencé, son brouillon devient inaccessible.',
        confirmLabel: 'Renvoyer quand même',
      });
      if (!ok) return;
    }
    setEnvoi(e => ({ ...e, busy: true }));
    let envoye = false;
    try {
      const r = await fetch(`${BASE}/forms/${f.id}/envoyer`, {
        method: 'POST', headers: jsonH(), body: JSON.stringify({ resource_id: Number(cible.id) }) });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && !j.error) { showNotification?.('L\'envoi des formulaires arrive côté serveur — rien n\'est parti.', 'info'); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const d = j.data ?? j;
      setLien({ url: d.lien || d.url || '', email_envoye: d.email_envoye, motif: d.email_erreur || j.message, qui: nomDe(cible) });
      setCopie(false);
      envoye = true;
      // L'email n'est PAS parti : on ne dit pas « envoyé ». Sans ça on
      // attendrait une réponse à un message que personne n'a reçu.
      if (d.email_envoye === false) showNotification?.(d.email_erreur || 'Le lien est prêt mais l\'email n\'est pas parti — transmettez-le vous-même.', 'error');
      else showNotification?.(j.message || d.message || 'Formulaire envoyé.', 'success');
      await charger();
      // Le suivi s'ouvre SUR l'envoi qu'on vient de faire : c'est le moment
      // où l'on veut voir la ligne apparaître, pas un clic plus tard.
      setOuverts(prev => new Set(prev).add(f.id));
      await chargerEnvois(f);
    } catch (e) { showNotification?.(e.message, 'error'); }
    // Le panneau ne se ferme QUE si quelque chose est parti : sur un refus, on
    // reste sur le destinataire choisi pour corriger, pas devant une liste.
    finally { setEnvoi(x => (envoye ? null : (x ? { ...x, busy: false } : x))); }
  };

  // QUI L'A REÇU. Un compte de réponses ne se relance pas : la seule question
  // qu'on se pose après un envoi est « qui n'a pas encore répondu ? ».
  const chargerEnvois = useCallback(async (f) => {
    setEnvois(e => ({ ...e, [f.id]: 'chargement' }));
    try {
      const r = await fetch(`${BASE}/forms/${f.id}/envois`, { headers: authH() });
      if ([404, 405, 501].includes(r.status)) { setEnvois(e => ({ ...e, [f.id]: 'absent' })); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setEnvois(e => ({ ...e, [f.id]: Array.isArray(j.data) ? j.data : [] }));
    } catch { setEnvois(e => ({ ...e, [f.id]: 'absent' })); }
  }, []);

  // Le suivi est REPLIÉ par défaut : c'est une vérification, pas la raison
  // d'ouvrir l'écran. On ne le charge qu'à l'ouverture — une liste par carte
  // au chargement, ce serait autant de requêtes pour rien.
  const basculerEnvois = (f) => {
    const ouvre = !ouverts.has(f.id);
    setOuverts(prev => { const s2 = new Set(prev); if (ouvre) s2.add(f.id); else s2.delete(f.id); return s2; });
    if (ouvre && envois[f.id] === undefined) chargerEnvois(f);
  };

  const copier = async () => {
    try { await navigator.clipboard.writeText(lien.url); setCopie(true); }
    catch { showNotification?.('Copie impossible — sélectionnez le lien à la main.', 'error'); }
  };

  const usageChoisi = useMemo(() => usages.find(u => u.cle === (form?.usage || '')) || null, [usages, form]);
  const usageDe = (f) => usages.find(u => u.cle === f.usage) || null;
  const prises = useMemo(() => new Set((form?.champs || []).map(c => c.destination).filter(Boolean)), [form]);

  return (
    <div className="forms-page">
      <header className="forms-tete">
        <div>
          <p className="forms-kicker">Formulaires en ligne</p>
          <h1>Forms<span>.</span></h1>
          <p className="forms-sous">Composez un formulaire, envoyez-le par lien. Les réponses reviennent en PDF et remplissent la fiche.</p>
        </div>
        {!form && <button type="button" className="erp-btn erp-btn--primary" onClick={() => setForm(formVide())}><BI icon="plus-lg" /> Nouveau formulaire</button>}
      </header>

      {/* Le parcours, dit une fois : c'est ce qui rend la colonne
          « Atterrit dans » compréhensible avant même d'éditer un champ. */}
      <ol className="forms-parcours" aria-label="Parcours d'un formulaire">
        <li><BI icon="ui-checks" /><span>Vous composez</span></li>
        <li><BI icon="link-45deg" /><span>Un lien part</span></li>
        <li><BI icon="pencil-square" /><span>La personne remplit en ligne</span></li>
        <li><BI icon="file-earmark-pdf" /><span>Le PDF rempli revient</span></li>
        <li><BI icon="person-vcard" /><span>Les réponses remplissent la fiche</span></li>
      </ol>

      {absent && <div className="forms-note">Les formulaires arrivent côté serveur. L'écran est prêt : vos compositions aboutiront dès la mise en ligne, rien n'est enregistré d'ici là.</div>}
      {erreur && <div className="forms-erreur" role="alert">{erreur}</div>}
      {champErreur && !/^champs\[/.test(champErreur.champ || '') && <div className="forms-erreur" role="alert">{champErreur.message}</div>}

      {lien && (
        <div className="forms-lien" role="status">
          <BI icon={lien.email_envoye === false ? 'exclamation-triangle-fill' : 'link-45deg'} />
          <div>
            <strong>{lien.email_envoye === false
              ? `Lien prêt pour ${lien.qui || 'le destinataire'} — l'email n'est pas parti`
              : `Lien envoyé à ${lien.qui || 'le destinataire'}`}</strong>
            {lien.motif && lien.email_envoye === false && <span className="forms-lien-motif">{lien.motif}</span>}
            <code className="erp-mono">{lien.url || '—'}</code>
          </div>
          {lien.url && <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={copier}>{copie ? 'Copié' : 'Copier le lien'}</button>}
          <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setLien(null)}>Fermer</button>
        </div>
      )}

      {/* ── À QUI ? ────────────────────────────────────────────────────────
          Le lien est personnel et les réponses atterrissent sur la fiche du
          destinataire : le choisir n'est pas une formalité, c'est l'envoi. */}
      {envoi && (
        <section className="forms-envoi" aria-label={`Envoyer ${envoi.form.nom}`}>
          <div className="forms-envoi-tete">
            <div>
              <p className="forms-kicker">Envoyer</p>
              <h2>{envoi.form.nom}</h2>
            </div>
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setEnvoi(null)}>Annuler</button>
          </div>
          {ressources === null ? (
            <p className="forms-envoi-aide"><BI icon="arrow-repeat" /> Lecture des fiches…</p>
          ) : ressourcesRefus ? (
            <p className="forms-envoi-aide">
              L'envoi d'un formulaire est réservé aux administrateurs : c'est un email personnel qui part,
              et les réponses écrivent sur une fiche. Demandez à un administrateur de l'envoyer.
            </p>
          ) : ressources.length === 0 ? (
            <p className="forms-envoi-aide">
              Aucune fiche ressource à qui envoyer ce formulaire. Créez d'abord la fiche de la personne —
              c'est elle qui recevra les réponses.
            </p>
          ) : (
            <>
              <label className="forms-envoi-champ">
                À qui
                <ErpiaSelect value={envoi.resource_id} aria-label="Destinataire du formulaire"
                  onChange={e => setEnvoi(x => ({ ...x, resource_id: e.target.value }))}>
                  <option value="">Choisissez la personne…</option>
                  {ressources.map(r => (
                    <option key={r.id} value={r.id} disabled={!r.email}>
                      {nomDe(r)}{r.email ? ` — ${r.email}` : ' — sans email'}
                    </option>
                  ))}
                </ErpiaSelect>
              </label>
              {/* Une fiche sans email est proposée GRISÉE, pas cachée : la
                  personne existe, on doit comprendre pourquoi on ne peut pas
                  lui écrire — et aller compléter sa fiche. */}
              <p className="forms-envoi-aide">
                {envoi.resource_id
                  ? 'Elle recevra un lien personnel, valable une seule fois. Ses réponses rempliront sa fiche.'
                  : 'Les fiches sans email sont grisées : complétez leur fiche pour pouvoir leur écrire.'}
              </p>
              <button type="button" className="erp-btn erp-btn--primary" disabled={!envoi.resource_id || envoi.busy} onClick={envoyer}>
                <BI icon={envoi.busy ? 'arrow-repeat' : 'send'} style={envoi.busy ? { animation: 'ats-spin .7s linear infinite' } : undefined} />
                {envoi.busy ? 'Envoi…' : 'Envoyer le formulaire'}
              </button>
            </>
          )}
        </section>
      )}

      {form ? (
        <section className="forms-editeur">
          <div className="forms-editeur-tete">
            <label>Nom du formulaire *
              <input className="erp-field" value={form.nom} autoFocus placeholder="Fiche employé — entrée en fonction"
                onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} />
            </label>
            <label>À quoi il sert
              <input className="erp-field" value={form.description} placeholder="Envoyé au collaborateur avant l'établissement du contrat"
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </label>
            {usages.length > 0 && (
              <label>Quand il part
                <ErpiaSelect value={form.usage || ''} onChange={e => setForm(f => ({ ...f, usage: e.target.value }))} aria-label="Usage du formulaire">
                  <option value="">Sur demande — vous l'envoyez vous-même</option>
                  {usages.map(u => <option key={u.cle} value={u.cle}>{u.libelle}</option>)}
                </ErpiaSelect>
                {usageChoisi && (
                  <small className="forms-usage-aide" data-auto={usageChoisi.automatique ? 'true' : undefined}>
                    {usageChoisi.automatique
                      ? `Le placement envoie ce formulaire seul, sans qu'on le choisisse.${usageChoisi.description ? ` ${usageChoisi.description}` : ''}`
                      : usageChoisi.description || 'Proposé en priorité dans ce contexte.'}
                  </small>
                )}
              </label>
            )}
          </div>

          <div className="forms-champs">
            <div className="forms-champ forms-champ--tete">
              <span>Question posée</span><span>Type</span><span>Atterrit dans</span><span>Requis</span><span />
            </div>
            {form.champs.map((c, i) => {
              const fautif = champErreur?.champ?.startsWith(`champs[${i}]`) ? champErreur : null;
              const sur = (suffixe) => fautif?.champ === `champs[${i}].${suffixe}`;
              return (
              <div className="forms-champ" key={c.id || i} data-faux={fautif ? 'true' : undefined}>
                <input className="erp-field" value={c.libelle} placeholder="Ex. Votre adresse complète"
                  aria-label={`Libellé du champ ${i + 1}`}
                  aria-invalid={sur('libelle') || undefined}
                  style={sur('libelle') ? { borderColor: 'var(--danger-bd)' } : undefined}
                  onChange={e => { majChamp(i, { libelle: e.target.value }); if (fautif) setChampErreur(null); }} />
                <ErpiaSelect value={c.type} onChange={e => majChamp(i, { type: e.target.value })} aria-label={`Type du champ ${i + 1}`}>
                  {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </ErpiaSelect>
                <ErpiaSelect value={c.destination || ''} aria-label={`Destination du champ ${i + 1}`}
                  style={sur('destination') ? { borderColor: 'var(--danger-bd)' } : undefined}
                  onChange={e => { const d = e.target.value; majChamp(i, { destination: d, ...(TYPE_PAR_CLE[d] ? { type: TYPE_PAR_CLE[d] } : {}) }); }}>
                  <option value="">Réponse libre — PDF seulement</option>
                  {DESTINATIONS.map(([k, l]) => (
                    <option key={k} value={k} disabled={prises.has(k) && c.destination !== k}>Fiche ressource · {l}</option>
                  ))}
                </ErpiaSelect>
                <label className="forms-requis">
                  <input type="checkbox" checked={!!c.obligatoire} onChange={e => majChamp(i, { obligatoire: e.target.checked })}
                    aria-label={`Champ ${i + 1} obligatoire`} />
                </label>
                <div className="forms-champ-actions">
                  <button type="button" onClick={() => deplacer(i, -1)} disabled={i === 0} aria-label={`Monter le champ ${i + 1}`}><BI icon="chevron-up" /></button>
                  <button type="button" onClick={() => deplacer(i, 1)} disabled={i === form.champs.length - 1} aria-label={`Descendre le champ ${i + 1}`}><BI icon="chevron-down" /></button>
                  <button type="button" onClick={() => setForm(f => ({ ...f, champs: f.champs.filter((_, j) => j !== i) }))}
                    disabled={form.champs.length === 1} aria-label={`Retirer le champ ${i + 1}`}><BI icon="x" /></button>
                </div>
                {c.type === 'choix' && (
                  <textarea className="erp-field forms-options" rows={3} value={c.options || ''}
                    placeholder={'Un choix par ligne\nCélibataire\nMarié·e'}
                    aria-label={`Choix possibles du champ ${i + 1}`}
                    aria-invalid={sur('options') || undefined}
                    style={sur('options') ? { borderColor: 'var(--danger-bd)' } : undefined}
                    onChange={e => { majChamp(i, { options: e.target.value }); if (fautif) setChampErreur(null); }} />
                )}
                {fautif && <span className="forms-champ-erreur" role="alert">{fautif.message}</span>}
              </div>
              );
            })}
          </div>

          <div className="forms-editeur-pied">
            <button type="button" className="erp-btn erp-btn--secondary" onClick={() => setForm(f => ({ ...f, champs: [...f.champs, champVide()] }))}>
              <BI icon="plus-lg" /> Ajouter un champ
            </button>
            <label className="forms-defaut" title={form.usage ? 'Parmi les formulaires de cet usage.' : 'Un seul formulaire sans usage peut être le défaut général.'}>
              <input type="checkbox" checked={!!form.defaut} onChange={e => setForm(f => ({ ...f, defaut: e.target.checked }))} />
              {form.usage ? 'Le retenir pour cet usage' : 'Proposer par défaut à l\'envoi'}
            </label>
            <span className="forms-editeur-note">
              {prises.size
                ? `${prises.size} réponse${prises.size > 1 ? 's' : ''} rempliront la fiche ressource ; les autres ne vivront que dans le PDF.`
                : 'Aucune réponse ne remplira la fiche ressource — choisissez une destination pour qu\'elles atterrissent quelque part.'}
            </span>
            <button type="button" className="erp-btn erp-btn--secondary" onClick={() => setForm(null)}>Annuler</button>
            <button type="button" className="erp-btn erp-btn--primary" onClick={enregistrer} disabled={busy}>
              {busy ? 'Enregistrement…' : form.id != null ? 'Enregistrer les modifications' : 'Créer le formulaire'}
            </button>
          </div>
        </section>
      ) : liste === null ? (
        <div className="forms-note"><BI icon="arrow-repeat" /> Chargement…</div>
      ) : liste.length === 0 ? (
        <div className="forms-vide">
          <BI icon="ui-checks" />
          <p>Aucun formulaire pour l'instant.</p>
          <p className="forms-vide-aide">Le premier est tout trouvé : la fiche employé, envoyée avant le contrat pour récupérer adresse, date de naissance, n° AVS et IBAN.</p>
          <button type="button" className="erp-btn erp-btn--primary" onClick={() => setForm({ ...formVide(), nom: 'Fiche employé — entrée en fonction', description: 'Envoyé au collaborateur avant l\'établissement du contrat', champs: DESTINATIONS.map(([cle, libelle]) => ({ ...champVide(), id: `p_${cle}`, libelle, destination: cle, type: TYPE_PAR_CLE[cle] || 'texte' })) })}>
            Composer la fiche employé
          </button>
        </div>
      ) : (
        <div className="forms-liste">
          {liste.map(f => (
            <article className="forms-carte" key={f.id}>
              <div className="forms-carte-tete">
                <h2>{f.nom}</h2>
                {usageDe(f) && <span className="erp-chip forms-chip-usage" title={usageDe(f).description || undefined}>{usageDe(f).libelle}</span>}
                {usageDe(f)?.automatique && <span className="erp-chip forms-chip-auto" title="Envoyé par le placement sans intervention"><BI icon="arrow-return-right" /> Automatique</span>}
                {f.defaut && <span className="erp-chip forms-chip-defaut">Par défaut</span>}
                {f.actif === false && <span className="erp-chip forms-chip-inactif">Désactivé</span>}
                <span className="erp-chip">{(f.champs || []).length} champ{(f.champs || []).length > 1 ? 's' : ''}</span>
              </div>
              {f.description && <p className="forms-carte-desc">{f.description}</p>}
              <p className="forms-carte-meta erp-mono">
                {(f.champs || []).filter(c => c.destination).length} vers la fiche
                {f.reponses != null ? ` · ${f.reponses} réponse${f.reponses > 1 ? 's' : ''}` : ''}
                {fmtDate(f.dernier_envoi) ? ` · envoyé le ${fmtDate(f.dernier_envoi)}` : ''}
              </p>
              <div className="forms-carte-actions">
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setForm({ ...f, champs: (f.champs || []).map((c, i) => ({ ...champVide(), ...c, id: c.id ?? `e${i}`, options: Array.isArray(c.options) ? c.options.join('\n') : (c.options || '') })) })}><BI icon="pencil" /> Modifier</button>
                {f.actif !== false && <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => ouvrirEnvoi(f)}><BI icon="send" /> Envoyer</button>}
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" aria-expanded={ouverts.has(f.id)} onClick={() => basculerEnvois(f)}>
                  <BI icon={ouverts.has(f.id) ? 'chevron-up' : 'people'} /> Qui l'a reçu
                </button>
                {f.actif === false && <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => basculerActif(f, true)}><BI icon="arrow-counterclockwise" /> Réactiver</button>}
                <button type="button" className="erp-btn erp-btn--danger erp-btn--sm" onClick={() => supprimer(f)}><BI icon="trash3" /> Supprimer</button>
              </div>
              {ouverts.has(f.id) && (
                <div className="forms-suivi">
                  {envois[f.id] === 'chargement' ? (
                    <p className="forms-suivi-vide"><BI icon="arrow-repeat" /> Lecture des envois…</p>
                  ) : envois[f.id] === 'absent' ? (
                    <p className="forms-suivi-vide">Le suivi des envois arrive côté serveur.</p>
                  ) : !(envois[f.id] || []).length ? (
                    <p className="forms-suivi-vide">Ce formulaire n'a encore été envoyé à personne.</p>
                  ) : (
                    (envois[f.id] || []).map(e => {
                      const [etat, icone, ton] = ETAT_ENVOI[e.statut] || [e.statut, 'dot', 'attente'];
                      return (
                        <div className="forms-suivi-ligne" key={e.id}>
                          <BI icon={icone} />
                          <span className="forms-suivi-qui">{nomDe(e.destinataire)}</span>
                          <span className="forms-suivi-etat" data-ton={ton}>{etat}</span>
                          <span className="forms-suivi-quand erp-mono">
                            {e.statut === 'recu' ? (fmtDate(e.recu_le) || '') : (fmtDate(e.envoye_le) || '')}
                            {e.champs_total ? ` · ${e.champs_remplis}/${e.champs_total}` : ''}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
